import React, { useState } from "react";
import CSVImport from "./CSVImport";
import { useUserPreferences } from "../contexts/UserPreferencesContext";

export const UserManagement = ({
  users,
  models,
  isLoading,
  editingUser,
  newUser,
  selectedUsers,
  setSelectedUsers,
  handleCreateUser,
  handleEditUser,
  handleSaveUser,
  handleDeleteUser,
  setNewUser,
  setEditingUser,
  fetchUsers,
  fetchWithAuth,
  setError,
  translations: t,
}) => {
  const [sortField, setSortField] = useState("username");
  const [sortDirection, setDirection] = useState("asc");
  const [filterText, setFilterText] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const { theme } = useUserPreferences();
  const isDark = theme === "dark";

  const handleSelectAll = () => {
    const newSelected = new Set(selectedUsers);
    const currentlyVisible = new Set(
      filteredAndSortedUsers.map((u) => u.username)
    );

    let allCurrentlySelectedVisible = true;
    currentlyVisible.forEach((username) => {
      if (!selectedUsers.has(username)) {
        allCurrentlySelectedVisible = false;
      }
    });

    if (allCurrentlySelectedVisible) {
      currentlyVisible.forEach((username) => {
        newSelected.delete(username);
      });
    } else {
      currentlyVisible.forEach((username) => {
        newSelected.add(username);
      });
    }

    setSelectedUsers(newSelected);
  };

  const handleSelectUser = (username) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(username)) {
      newSelected.delete(username);
    } else {
      newSelected.add(username);
    }
    setSelectedUsers(newSelected);
  };

  const filteredAndSortedUsers = [...users]
    .filter((user) => {
      const textMatch =
        user.username?.toLowerCase().includes(filterText.toLowerCase()) ||
        user.email?.toLowerCase().includes(filterText.toLowerCase()) ||
        user.Role?.toLowerCase().includes(filterText.toLowerCase());

      const roleMatch =
        roleFilter === "all" ||
        user.Role?.toLowerCase() === roleFilter.toLowerCase();

      return textMatch && roleMatch;
    })
    .sort((a, b) => {
      const aVal = a[sortField]?.toString().toLowerCase();
      const bVal = b[sortField]?.toString().toLowerCase();
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

  return (
    <div
      className={`${
        isDark ? "bg-gray-800" : "bg-white"
      } rounded-lg shadow-sm p-6`}
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2
            className={`text-xl font-semibold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {t.userManagement}
          </h2>
          <p className="text-sm text-blue-500 mt-1">
            {t.selected}: {selectedUsers.size}/{users.length} {t.usersSelected}
          </p>
        </div>
        <div className="flex gap-4">
          <select
            className={`border rounded p-2 ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">{t.allRoles}</option>
            <option value="users">{t.usersOnly}</option>
            <option value="admins">{t.adminsOnly}</option>
          </select>
          <select
            className={`border rounded p-2 ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
          >
            <option value="username">
              {t.sortBy} {t.username}
            </option>
            <option value="email">
              {t.sortBy} {t.email}
            </option>
            <option value="Role">
              {t.sortBy} {t.role}
            </option>
          </select>
          <button
            onClick={() => setDirection((d) => (d === "asc" ? "desc" : "asc"))}
            className={`px-3 py-1 border rounded ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                : "bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            {sortDirection === "asc" ? t.ascending : t.descending}
          </button>
          <input
            type="text"
            placeholder={`${t.filter}...`}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className={`border rounded p-2 ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                : "bg-white border-gray-300"
            }`}
          />
        </div>
      </div>

      {/* Create New User Form */}
      <div
        className={`mt-8 pt-6 border-t ${
          isDark ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <h3
          className={`text-lg font-medium mb-4 ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          {t.createNewUser}
        </h3>
        <form onSubmit={handleCreateUser} className="space-y-6">
          {/* Form inputs */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-6 rounded-lg p-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            {/* Username */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {t.username}:
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) =>
                  setNewUser({ ...newUser, username: e.target.value })
                }
                className={`w-full p-2 border rounded ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "border-gray-300"
                }`}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {t.email}:
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
                className={`w-full p-2 border rounded ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "border-gray-300"
                }`}
                required
              />
            </div>

            {/* Password */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {t.password}:
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
                className={`w-full p-2 border rounded ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "border-gray-300"
                }`}
                required
              />
            </div>

            {/* Group */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {t.group}:
              </label>
              <select
                value={newUser.group}
                onChange={(e) =>
                  setNewUser({ ...newUser, group: e.target.value })
                }
                className={`w-full p-2 border rounded ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "border-gray-300"
                }`}
              >
                <option value="Users">{t.users}</option>
                <option value="Admins">{t.admins}</option>
              </select>
            </div>

            {/* Quota */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {t.quota}:
              </label>
              <input
                type="number"
                value={newUser.quota}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    quota: parseInt(e.target.value) || 0,
                  })
                }
                className={`w-full p-2 border rounded ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "border-gray-300"
                }`}
                min="0"
              />
            </div>

            {/* Assigned Models */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-700"
                }`}
              >
                {t.assignedModels}:
              </label>
              <select
                multiple
                value={newUser.assignedModels.map((model) => model.S || model)}
                onChange={(e) =>
                  setNewUser({
                    ...newUser,
                    assignedModels: Array.from(
                      e.target.selectedOptions,
                      (option) => ({ S: option.value })
                    ),
                  })
                }
                className={`w-full p-2 border rounded min-h-[100px] ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "border-gray-300"
                }`}
              >
                {models.map((model) => (
                  <option key={model.modelId} value={model.name}>
                    {model.name}
                  </option>
                ))}
              </select>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {t.holdCtrlToSelect}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {t.createUser}
            </button>
          </div>
        </form>

        <CSVImport
          onUsersCreated={fetchUsers}
          fetchUsers={fetchUsers}
          translations={t}
        />
      </div>

      {/* Users List */}
      <div className="mt-8 space-y-4">
        {/* Desktop headers */}
        <div
          className={`hidden md:grid grid-cols-12 gap-4 px-4 py-2 rounded-lg font-medium ${
            isDark ? "bg-gray-700" : "bg-gray-100"
          }`}
          style={{
            gridTemplateColumns: "20px 140px 260px 100px 80px 1fr 120px",
          }}
        >
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={
                filteredAndSortedUsers.length > 0 &&
                filteredAndSortedUsers.every((user) =>
                  selectedUsers.has(user.username)
                )
              }
              onChange={handleSelectAll}
              className={`w-4 h-4 rounded border-gray-300 ${
                isDark
                  ? "bg-gray-800 border-gray-600 text-blue-500"
                  : "text-blue-600 focus:ring-blue-500"
              }`}
            />
          </div>
          <div className={isDark ? "text-gray-200" : "text-gray-700"}>
            {t.username}
          </div>
          <div className={isDark ? "text-gray-200" : "text-gray-700"}>
            {t.email}
          </div>
          <div className={isDark ? "text-gray-200" : "text-gray-700"}>
            {t.status}
          </div>
          <div className={isDark ? "text-gray-200" : "text-gray-700"}>
            {t.role}
          </div>
          <div className={isDark ? "text-gray-200" : "text-gray-700"}>
            {t.models}
          </div>
          <div className={isDark ? "text-gray-200" : "text-gray-700"}>
            {t.actions}
          </div>
        </div>

        {/* User rows */}
        {filteredAndSortedUsers.map((user) => (
          <div
            key={user.username}
            className={`grid gap-4 px-4 py-3 rounded-lg border transition-colors duration-200 ${
              selectedUsers.has(user.username)
                ? isDark
                  ? "bg-blue-900/30 border-blue-800"
                  : "bg-blue-50 border-blue-200"
                : isDark
                ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                : "bg-white border-gray-200 hover:bg-gray-50"
            }`}
            style={{
              gridTemplateColumns: "20px 140px 260px 100px 80px 1fr 120px",
            }}
          >
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedUsers.has(user.username)}
                onChange={() => handleSelectUser(user.username)}
                className={`w-4 h-4 rounded border-gray-300 ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-blue-500"
                    : "text-blue-600 focus:ring-blue-500"
                }`}
              />
            </div>
            <div
              className={`truncate ${
                isDark ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {user.username}
            </div>
            <div
              className={`truncate ${
                isDark ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {user.email}
            </div>
            <div>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-sm ${
                  user.userStatus === "FORCE_CHANGE_PASSWORD"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {user.userStatus === "FORCE_CHANGE_PASSWORD"
                  ? t.notActive
                  : user.userStatus === "CONFIRMED"
                  ? t.active
                  : user.userStatus || t.active}
              </span>
            </div>
            <div className={isDark ? "text-gray-200" : "text-gray-900"}>
              {user.Role || t.users}
            </div>
            <div
              className={`truncate ${
                isDark ? "text-gray-200" : "text-gray-900"
              }`}
            >
              {user.AssignedModels?.length > 0
                ? user.AssignedModels.map((model) => model.S || model).join(
                    ", "
                  )
                : t.none}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleEditUser(user)}
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors duration-200"
              >
                {t.edit}
              </button>
              <button
                onClick={() => handleDeleteUser(user.username)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors duration-200"
              >
                {t.delete}
              </button>
            </div>
          </div>
        ))}
        {filteredAndSortedUsers.length === 0 && (
          <div
            className={`text-center p-4 ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            No users found
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div
            className={`rounded-lg p-6 max-w-2xl w-full ${
              isDark ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-xl font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Edit User: {editingUser.username}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Email:
                </label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, email: e.target.value })
                  }
                  className={`w-full p-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                      : "border-gray-300 focus:ring-blue-500 hover:bg-gray-50"
                  } transition-colors duration-200`}
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Group:
                </label>
                <select
                  value={editingUser.group}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, group: e.target.value })
                  }
                  className={`w-full p-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                      : "border-gray-300 focus:ring-blue-500 hover:bg-gray-50"
                  } transition-colors duration-200`}
                >
                  <option value="Users">Users</option>
                  <option value="Admins">Admins</option>
                </select>
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Quota:
                </label>
                <input
                  type="number"
                  value={editingUser.quota}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      quota: parseInt(e.target.value) || 0,
                    })
                  }
                  className={`w-full p-2 border rounded ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                      : "border-gray-300 focus:ring-blue-500 hover:bg-gray-50"
                  } transition-colors duration-200`}
                  min="0"
                />
              </div>
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Assigned Models:
                </label>
                <select
                  multiple
                  value={editingUser.assignedModels.map(
                    (model) => model.S || model
                  )}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      assignedModels: Array.from(
                        e.target.selectedOptions,
                        (option) => ({ S: option.value })
                      ),
                    })
                  }
                  className={`w-full p-2 border rounded min-h-[100px] ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                      : "border-gray-300 focus:ring-blue-500 hover:bg-gray-50"
                  } transition-colors duration-200`}
                >
                  {models.map((model) => (
                    <option key={model.modelId} value={model.name}>
                      {model.name}
                    </option>
                  ))}
                </select>
                <p
                  className={`text-sm mt-1 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Hold Ctrl/Cmd to select multiple models
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => handleSaveUser(editingUser.username)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200"
              >
                Save
              </button>
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
